import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TouchpointDTOContentType,
  TouchpointCreateRequestPopulationType,
  TouchpointCreateRequestType,
} from "generated/schemas";
import { TouchpointType } from "types/touchpoint";
import { getActionTouchpointNameLabel } from "utilities";

export const COPY_CODE_MAX_LENGTH = 32;

export const getRequiredFieldValidation = (name: string) => {
  const message = `${name} is required`;
  return z.string({ required_error: message }).nonempty({ message });
};

const contentTypeValidation = z.nativeEnum(TouchpointDTOContentType, {
  errorMap: () => ({ message: "Content type is required" }),
});

export const populationTypeValidation = z.nativeEnum(TouchpointCreateRequestPopulationType, {
  errorMap: () => ({ message: "Population type is required" }),
});

export const classesValidation = z
  .array(z.string())
  .min(1, { message: "At least one class is required" });

// The backend requires copyCode on every touchpoint create/update (TouchpointCreateRequest,
// TouchpointVersionBaseRequest) — never on patch, which is why the edit-touchpoint/edit-email
// schemas below (PUT, full-replace semantics) need it too, not just the add/clone flows.
export const copyCodeField = z
  .string()
  .trim()
  .min(1, { message: "Copy code is required" })
  .max(COPY_CODE_MAX_LENGTH, {
    message: "Please do not exceed character limit.",
  });

export const requiredFiscalYearField = z.number({
  required_error: "Fiscal year is required",
  invalid_type_error: "Fiscal year is required",
});

export const EMAIL_FORM_VALIDATION_SCHEMA = zodResolver(
  z.object({
    emailTemplateId: z.string().optional(),
    name: getRequiredFieldValidation("Email name"),
    populationType: populationTypeValidation,
    contentType: contentTypeValidation,
    applyClientTheme: z.boolean().optional(),
    folderId: z.string().optional(),
    fiscalYear: requiredFiscalYearField,
    copyCode: copyCodeField,
    insightsActivator: z.boolean().optional(),
    classes: classesValidation,
  }),
);

export const getCloneTouchpointSchema = (type: TouchpointType | TouchpointCreateRequestType) =>
  zodResolver(
    z.object({
      campaignId: getRequiredFieldValidation("Campaign name"),
      touchpointName: getRequiredFieldValidation(getActionTouchpointNameLabel(type)),
      populationType: populationTypeValidation,
      contentType: getRequiredFieldValidation("Content type"),
      fiscalYear: requiredFiscalYearField,
      copyCode: copyCodeField,
      applyClientTheme: z.boolean().optional(),
      folderId: z.string().optional(),
      insightsActivator: z.boolean().optional(),
      classes: classesValidation,
    }),
  );

export const EDIT_EMAIL_FORM_VALIDATION_SCHEMA = zodResolver(
  z.object({
    name: getRequiredFieldValidation("Email name"),
    populationType: populationTypeValidation,
    contentType: contentTypeValidation,
    campaignId: z.string().optional(),
    copyCode: copyCodeField,
    folderId: z.string().optional(),
    insightsActivator: z.boolean().optional(),
    classes: classesValidation,
  }),
);

export const ADD_TOUCHPOINT_FORM_VALIDATION_SCHEMA = zodResolver(
  z.object({
    touchpointTemplateId: z.string().optional(),
    name: getRequiredFieldValidation("Creative name"),
    populationType: populationTypeValidation,
    contentType: contentTypeValidation,
    folderId: z.string().optional(),
    fiscalYear: requiredFiscalYearField,
    copyCode: copyCodeField,
    insightsActivator: z.boolean().optional(),
    type: z.nativeEnum(TouchpointCreateRequestType).optional(),
    classes: classesValidation,
  }),
);

export const EDIT_TOUCHPOINT_FORM_VALIDATION_SCHEMA = zodResolver(
  z.object({
    name: getRequiredFieldValidation("Creative name"),
    populationType: populationTypeValidation,
    contentType: contentTypeValidation,
    campaignId: z.string().optional(),
    copyCode: copyCodeField,
    folderId: z.string().optional(),
    insightsActivator: z.boolean().optional(),
    classes: classesValidation,
  }),
);
